import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  WarningOutlined,
  SafetyOutlined,
  DeploymentUnitOutlined
} from '@ant-design/icons';

// FIN-11360 — summary cards styled to the FRAML View mock (5 tiles, coloured
// numerals + label icons, Cross-Domain highlighted). Colours mirror the queue
// domain palette.
// `source` = the queue filter value the tile applies (null = not a filter tile).
const STATS = [
  { label: 'Customers', key: 'totalCustomers', tone: 'neutral', icon: null, source: null },
  { label: 'Total Alerts', key: 'totalAlerts', tone: 'neutral', icon: null, source: null },
  { label: 'Fraud', key: 'fraudCount', tone: 'fraud', icon: <WarningOutlined />, source: 'fraud' },
  { label: 'AML', key: 'amlCount', tone: 'aml', icon: <SafetyOutlined />, source: 'aml' },
  {
    label: 'Cross-Domain',
    key: 'crossDomainCount',
    tone: 'cross',
    icon: <DeploymentUnitOutlined />,
    source: 'cross-domain'
  }
];

// Tones measured off the mock (fincense-cm-db.lovable.app/framl-alerts):
// border #dcdfe5, neutral numeral #29303d. The coloured highlight (bgActive/
// borderActive) applies only when the tile's source is the active filter —
// Cross-Domain no longer looks pre-selected.
const TONE = {
  neutral: { num: '#29303d', bg: '#ffffff', border: '#dcdfe5', bgActive: '#ffffff', borderActive: '#dcdfe5' },
  fraud: { num: '#e0483b', bg: '#ffffff', border: '#dcdfe5', bgActive: '#fff0ef', borderActive: '#f3b5af' },
  aml: { num: '#2f6df6', bg: '#ffffff', border: '#dcdfe5', bgActive: '#eef3ff', borderActive: '#aac2fb' },
  cross: { num: '#7c4dff', bg: '#ffffff', border: '#dcdfe5', bgActive: '#f6f3ff', borderActive: '#e2d8ff' }
};

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin: 0 0 18px;
`;

const Card = styled.div`
  background: ${(p) => p.bg};
  border: 1px solid ${(p) => p.border};
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${(p) => (p.clickable ? 'pointer' : 'default')};
`;

const Num = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 28px;
  color: ${(p) => p.color};
  font-variant-numeric: tabular-nums;
`;

const Label = styled.div`
  margin-top: 4px;
  font-size: 11px;
  line-height: 16.5px;
  color: ${(p) => p.color || '#676f7e'};
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  .anticon {
    font-size: 11px;
  }
`;

function FramlHeaderStats({ summary, activeSource, onTileFilter }) {
  return (
    <Row>
      {STATS.map(({ label, key, tone, icon, source }) => {
        const t = TONE[tone];
        const clickable = Boolean(source);
        const active = clickable && activeSource === source;
        return (
          <Card
            key={key}
            data-tile={source || 'none'}
            bg={active ? t.bgActive : '#ffffff'}
            border={active ? t.borderActive : '#dcdfe5'}
            clickable={clickable}
            onClick={clickable ? () => onTileFilter(active ? '' : source) : undefined}
          >
            <Num color={t.num}>{summary[key] != null ? summary[key] : 0}</Num>
            <Label color={tone === 'neutral' ? '#6b7280' : t.num}>
              {icon}
              {label}
            </Label>
          </Card>
        );
      })}
    </Row>
  );
}

FramlHeaderStats.propTypes = {
  summary: PropTypes.shape({
    totalCustomers: PropTypes.number,
    totalAlerts: PropTypes.number,
    fraudCount: PropTypes.number,
    amlCount: PropTypes.number,
    crossDomainCount: PropTypes.number
  }),
  activeSource: PropTypes.string,
  onTileFilter: PropTypes.func
};

FramlHeaderStats.defaultProps = {
  summary: {},
  activeSource: '',
  onTileFilter: () => {}
};

export default FramlHeaderStats;
